import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import VN from "../data/vietnam.json";
import { api } from "../utils/api";

/* ---------------- Helpers tìm kiếm & hiển thị ---------------- */

// Bỏ dấu tiếng Việt
const deAccent = (s = "") =>
    s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[đĐ]/g, (m) => (m === "đ" ? "d" : "D"));

// Chuẩn key tìm kiếm
const normKey = (s = "") =>
    deAccent(String(s))
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

// Bỏ tiền tố đầu câu theo level
const RE_PROV = /^(tinh|thanh pho|tp\.?)/i;
const RE_DIST = /^(quan|huyen|thi xa|thi tran|tp\.?)/i;
const RE_WARD = /^(phuong|xa|thi tran)/i;

const stripPrefix = (name, level) => {
    const k = normKey(name);
    if (level === "province") return k.replace(RE_PROV, "").trim();
    if (level === "district") return k.replace(RE_DIST, "").trim();
    if (level === "ward") return k.replace(RE_WARD, "").trim();
    return k;
};

// Build “túi từ khoá” để search (bao gồm synonym)
const buildBlob = (name, level) => {
    const raw = normKey(name);
    const noPfx = stripPrefix(name, level);
    const bag = new Set([raw, noPfx]);

    // thành phố → tp
    bag.add(raw.replace(/^thanh pho\s+/, "tp "));
    bag.add(noPfx.replace(/^thanh pho\s+/, "tp "));

    // quận → q ; huyện → h ; thị xã → tx
    bag.add(raw.replace(/^quan\s+/, "q "));
    bag.add(noPfx.replace(/^quan\s+/, "q "));
    bag.add(raw.replace(/^huyen\s+/, "h "));
    bag.add(noPfx.replace(/^huyen\s+/, "h "));
    bag.add(raw.replace(/^thi xa\s+/, "tx "));
    bag.add(noPfx.replace(/^thi xa\s+/, "tx "));

    return Array.from(bag).join(" ");
};

const titleCase = (s = "") =>
    String(s)
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

// 5 thành phố trực thuộc TW
const centralCities = new Set([
    "ho chi minh",
    "ha noi",
    "da nang",
    "can tho",
    "hai phong",
]);

// Nhãn thân thiện cho tỉnh/thành
const formatProvince = (raw) => {
    const k = stripPrefix(raw, "province");
    const disp = titleCase(k);
    const isCentral = centralCities.has(stripPrefix(raw, "province"));
    return isCentral ? `TP. ${disp}` : disp; // "TP. Hồ Chí Minh" / "Bình Dương"
};

// Nhãn thân thiện cho quận/huyện
const formatDistrict = (raw) => {
    const k = normKey(raw);
    if (/^quan\s+/.test(k)) return `Quận ${titleCase(k.replace(/^quan\s+/, ""))}`;
    if (/^huyen\s+/.test(k)) return `Huyện ${titleCase(k.replace(/^huyen\s+/, ""))}`;
    if (/^thi xa\s+/.test(k)) return `Thị xã ${titleCase(k.replace(/^thi xa\s+/, ""))}`;
    return titleCase(stripPrefix(raw, "district"));
};

// Nhãn thân thiện cho xã/phường
const formatWard = (raw) => {
    const k = normKey(raw);
    if (/^phuong\s+/.test(k)) return `P. ${titleCase(k.replace(/^phuong\s+/, ""))}`;
    if (/^xa\s+/.test(k)) return `X. ${titleCase(k.replace(/^xa\s+/, ""))}`;
    return titleCase(stripPrefix(raw, "ward"));
};

// Tìm trong mảng đã index
const searchItems = (items, q, limit = 150) => {
    const key = normKey(q);
    if (!key) return items.slice(0, limit);
    const out = [];
    for (const it of items) {
        if (it.search.includes(key)) {
            out.push(it);
            if (out.length >= limit) break;
        }
    }
    return out;
};

// uniq nhanh
const uniq = (arr) => Array.from(new Set(arr));

/* ---------------- Component ---------------- */

export function PostForm() {
    const [form, setForm] = useState({
        serviceId: "",
        title: "",
        content: "",
        phone: "",
        address: "",
        province: "",
        district: "",
        ward: "",
        google_maps: "",
        youtube: "",
    });
    const [saving, setSaving] = useState(false);

    // ===== Lấy danh sách dịch vụ =====
    const [services, setServices] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const r = await api("/services");
                const data = (r.items || r || []).sort((a, b) => Number(a.id) - Number(b.id));
                setServices(data);
            } catch (e) {
                console.error("Load services failed", e);
                toast.error("Không tải được danh sách dịch vụ.");
            }
        })();
    }, []);

    // ===== Index dữ liệu VN phẳng (province/district/ward) =====
    // Provinces
    const allProvinces = useMemo(() => {
        const raws = uniq(VN.map((r) => r.province));
        return raws.map((raw) => ({
            raw,                                 // giá trị gốc trong data
            label: formatProvince(raw),          // nhãn thân thiện hiển thị
            search: buildBlob(raw, "province"),  // blob để tìm
        }));
    }, []);

    // lấy ra "raw" tỉnh đang chọn (so sánh theo label hoặc raw – vì input lưu label)
    const selectedProvinceRaw = useMemo(() => {
        const p = allProvinces.find(
            (x) => x.label === form.province || x.raw === form.province
        );
        return p ? p.raw : null;
    }, [form.province, allProvinces]);

    // Districts theo tỉnh (nếu chưa chọn tỉnh thì cho tất cả – để ai đó vẫn có thể gõ)
    const allDistricts = useMemo(() => {
        const base = selectedProvinceRaw
            ? VN.filter((r) => r.province === selectedProvinceRaw)
            : VN;
        const raws = uniq(base.map((r) => r.district));
        return raws.map((raw) => ({
            raw,
            label: formatDistrict(raw),
            search: buildBlob(raw, "district"),
            // để filter ward theo district đã chọn
            provinceRaw: selectedProvinceRaw,
        }));
    }, [selectedProvinceRaw]);

    // raw quận/huyện đang chọn
    const selectedDistrictRaw = useMemo(() => {
        const d = allDistricts.find(
            (x) => x.label === form.district || x.raw === form.district
        );
        return d ? d.raw : null;
    }, [form.district, allDistricts]);

    // Ward context (dựa trên district/province đã chọn)
    const wardContext = useMemo(() => {
        let base = VN;
        if (selectedDistrictRaw) {
            base = base.filter((r) => r.district === selectedDistrictRaw);
            if (selectedProvinceRaw) base = base.filter((r) => r.province === selectedProvinceRaw);
        } else if (selectedProvinceRaw) {
            base = base.filter((r) => r.province === selectedProvinceRaw);
        }
        const seen = new Set();
        const out = [];
        for (const r of base) {
            const key = `${r.ward}|${r.district}|${r.province}`;
            if (!seen.has(key)) {
                seen.add(key);
                out.push({
                    ward: r.ward,
                    district: r.district,
                    province: r.province,
                    // hiển thị gợi ý (giữ nguyên value gốc để commit chính xác)
                    value: `${r.ward} | ${r.district} | ${r.province}`,
                    // nhãn đẹp nếu sau này bạn muốn dùng dropdown tự custom
                    label: `${formatWard(r.ward)} | ${formatDistrict(r.district)} | ${formatProvince(r.province)}`,
                    search: `${buildBlob(r.ward, "ward")} ${buildBlob(r.district, "district")} ${buildBlob(r.province, "province")}`,
                });
            }
        }
        return out;
    }, [selectedProvinceRaw, selectedDistrictRaw]);

    // ===== OPTIONS (sử dụng searchItems thay vì byText) =====
    const provOptions = useMemo(() => {
        return searchItems(allProvinces, form.province, 150);
    }, [form.province, allProvinces]);

    const distOptions = useMemo(() => {
        return searchItems(allDistricts, form.district, 150);
    }, [form.district, allDistricts]);

    const wardOptions = useMemo(() => {
        return searchItems(wardContext, form.ward, 150);
    }, [form.ward, wardContext]);

    // ===== onChange / commit =====
    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const commitProvince = () =>
        setForm((f) => ({ ...f, district: "", ward: "" }));

    const commitDistrict = () =>
        setForm((f) => ({ ...f, ward: "" }));

    // Khi blur ô ward, nếu người dùng chọn dòng “Ward | District | Province”
    // thì tự gán lại các field tương ứng (sử dụng “raw” để dò)
    const commitWard = () => {
        const t = form.ward.trim();
        if (!t) return;
        if (t.includes("|")) {
            const [w, d, p] = t.split("|").map((s) => s.trim());
            const ok = VN.find(
                (r) => r.ward === w && r.district === d && r.province === p
            );
            if (ok) {
                setForm((f) => ({
                    ...f,
                    ward: formatWard(ok.ward),
                    district: formatDistrict(ok.district),
                    province: formatProvince(ok.province),
                }));
            }
        } else {
            // Nếu chỉ gõ tên xã/phường (không chọn), thử tự tìm duy nhất trong wardContext
            const c = wardContext.filter((x) => normKey(x.ward) === normKey(t));
            if (c.length === 1) {
                const { ward, district, province } = c[0];
                setForm((f) => ({
                    ...f,
                    ward: formatWard(ward),
                    district: formatDistrict(district),
                    province: formatProvince(province),
                }));
            }
        }
    };

    // ===== Submit =====
    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.serviceId) return toast.error("Vui lòng chọn dịch vụ.");
        if (!form.title.trim()) return toast.error("Vui lòng nhập tiêu đề.");

        try {
            setSaving(true);

            const payload = {
                service_id: Number(form.serviceId),
                title: form.title.trim(),
                content: form.content,
                phone: form.phone,
                address: form.address,
                // Lưu chuỗi hiển thị gọn gàng
                area_province: form.province,
                area_district: form.district,
                area_ward: form.ward,
                google_maps: form.google_maps,
                youtube: form.youtube,
            };

            await api("/posts", { method: "POST", data: payload });
            toast.success("Tin đã được gửi, vui lòng chờ duyệt.");

            setForm({
                serviceId: "",
                title: "",
                content: "",
                phone: "",
                address: "",
                province: "",
                district: "",
                ward: "",
                google_maps: "",
                youtube: "",
            });
        } catch (err) {
            console.error(err);
            toast.error("Đăng tin thất bại.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-md mx-auto p-4 sm:p-6">
            <h1 className="text-lg font-bold mb-4">Đăng tin mới</h1>

            <form
                onSubmit={onSubmit}
                className="bg_white rounded-2xl border shadow-sm flex flex-col max-h-[85vh]"
            >
                <div className="p-4 space-y-4 overflow-y-auto">
                    <div>
                        <label className="block text-sm mb-1">Chọn dịch vụ *</label>
                        <select
                            name="serviceId"
                            value={form.serviceId}
                            onChange={onChange}
                            required
                            className="w-full p-3 border rounded-xl"
                        >
                            <option value="">-- Chọn 1 trong 45 dịch vụ --</option>
                            {services.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.id}. {s.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text_sm mb-1">Tiêu đề *</label>
                        <input
                            name="title"
                            value={form.title}
                            onChange={onChange}
                            className="w-full p-3 border rounded-xl"
                            placeholder="Nhập tiêu đề tin"
                            required
                            autoComplete="off"
                        />
                    </div>

                    <div>
                        <label className="block text-sm mb-1">Nội dung</label>
                        <textarea
                            name="content"
                            value={form.content}
                            onChange={onChange}
                            className="w-full p-3 border rounded-xl"
                            rows={4}
                        />
                    </div>

                    {/* Phone/Address */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                            name="phone"
                            value={form.phone}
                            onChange={onChange}
                            placeholder="Số điện thoại"
                            className="w-full p-3 border rounded-xl"
                            inputMode="tel"
                        />
                        <input
                            name="address"
                            value={form.address}
                            onChange={onChange}
                            placeholder="Địa chỉ"
                            className="w-full p-3 border rounded-xl"
                        />
                    </div>

                    {/* Province / District / Ward */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm mb-1">Tỉnh/TP</label>
                            <input
                                list="province-list"
                                name="province"
                                value={form.province}
                                onChange={onChange}
                                onBlur={commitProvince}
                                placeholder="Gõ: hcm, tp ho chi minh, ho chi…"
                                className="w-full p-3 border rounded-xl"
                            />
                            <datalist id="province-list">
                                {provOptions.map((p) => (
                                    <option key={p.raw} value={p.label} />
                                ))}
                            </datalist>
                        </div>

                        <div>
                            <label className="block text-sm mb-1">Quận/Huyện (tuỳ chọn)</label>
                            <input
                                list="district-list"
                                name="district"
                                value={form.district}
                                onChange={onChange}
                                onBlur={commitDistrict}
                                placeholder={
                                    selectedProvinceRaw
                                        ? "Gõ: q1, cu chi, thu duc…"
                                        : "Chọn Tỉnh trước (hoặc bỏ qua)"
                                }
                                className="w-full p-3 border rounded-xl"
                            />
                            <datalist id="district-list">
                                {distOptions.map((d) => (
                                    <option key={d.raw} value={d.label} />
                                ))}
                            </datalist>
                        </div>

                        <div className="sm:col-span-2">
                            <label className="block text-sm mb-1">
                                Xã/Phường (gợi ý: <i>Ward | District | Province</i>)
                            </label>
                            <input
                                list="ward-list"
                                name="ward"
                                value={form.ward}
                                onChange={onChange}
                                onBlur={commitWard}
                                placeholder="Gõ tên xã/phường; chọn dòng gợi ý để tự điền huyện/tỉnh"
                                className="w-full p-3 border rounded-xl"
                            />
                            <datalist id="ward-list">
                                {wardOptions.map((w) => (
                                    <option key={w.value} value={w.value} />
                                ))}
                            </datalist>
                        </div>
                    </div>

                    {/* Links */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                            name="google_maps"
                            value={form.google_maps}
                            onChange={onChange}
                            placeholder="Google Maps link"
                            className="w-full p-3 border rounded-xl"
                        />
                        <input
                            name="youtube"
                            value={form.youtube}
                            onChange={onChange}
                            placeholder="YouTube link"
                            className="w-full p-3 border rounded-xl"
                        />
                    </div>
                </div>

                <div className="p-3 border-t bg-white sticky bottom-0">
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full py-3 rounded-xl bg-red-600 text-white font-semibold disabled:opacity-60"
                    >
                        {saving ? "Đang đăng..." : "Đăng tin"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default PostForm;
